import React, { useCallback, useEffect, useMemo, useState } from "react";
import Seo from "../components/Seo";
import FreeConsultationForm from "../components/FreeConsultationForm";
import OnlineCoursesHero from "../components/online-courses/OnlineCoursesHero";
import PrivateYogaSection from "../components/online-courses/PrivateYogaSection";
import TrustReasonsSection from "../components/online-courses/TrustReasonsSection";
import TeacherDiscoverySection from "../components/online-courses/TeacherDiscoverySection";
import HowItWorksSection from "../components/online-courses/HowItWorksSection";
import ConsultationCtaSection from "../components/online-courses/ConsultationCtaSection";
import FinalCtaSection from "../components/online-courses/FinalCtaSection";
import StickyMobileCta from "../components/online-courses/StickyMobileCta";
import { searchRetreats, fetchTeacherBySlug } from "../services/api";
import { groupTeacherOfferingsForListing } from "../lib/groupTeacherListings";
import { buildOnlineTeacherCard } from "../lib/onlineTeacherCardModel";
import { ONLINE_COURSE } from "../lib/courseTypes";
import ONLINE_COURSES_SEO from "../data/onlineCoursesSeo.json";

// Shared with inject-seo.js so the prerendered <head> can never drift from the page.
const faqItems = ONLINE_COURSES_SEO.faq;

const DEFAULT_CONSULT_CONTEXT = {
  retreat_name: "Online Yoga Teachers",
  slug: "online-courses",
  location: "Online",
  askTeacher: true,
};

function scrollToId(id) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

function OnlineCourses() {
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeNeed, setActiveNeed] = useState(null);
  const [consultOpen, setConsultOpen] = useState(false);
  const [consultContext, setConsultContext] = useState(DEFAULT_CONSULT_CONTEXT);

  useEffect(() => {
    let cancelled = false;

    async function loadTeachers() {
      setLoading(true);
      try {
        const { items } = await searchRetreats({
          type: ONLINE_COURSE,
          limit: 200,
          offset: 0,
        });
        const grouped = groupTeacherOfferingsForListing(items, {
          hidePrices: false,
          teachersOnly: true,
        });

        const enriched = await Promise.all(
          grouped.map(async (card) => {
            const slug = card.instructor_slug;
            let profile = null;
            if (slug) {
              try {
                profile = await fetchTeacherBySlug(slug);
              } catch {
                profile = null;
              }
            }
            return buildOnlineTeacherCard(card, profile);
          })
        );

        if (!cancelled) {
          setTeachers(enriched.filter((t) => t.slug));
        }
      } catch {
        if (!cancelled) setTeachers([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadTeachers();
    return () => {
      cancelled = true;
    };
  }, []);

  const heroPhotos = useMemo(
    () => teachers.map((t) => t.photo).filter(Boolean).slice(0, 4),
    [teachers]
  );

  const openConsultation = useCallback((context = null) => {
    setConsultContext(context || DEFAULT_CONSULT_CONTEXT);
    setConsultOpen(true);
  }, []);

  const openGeneralConsultation = useCallback(() => {
    openConsultation(DEFAULT_CONSULT_CONTEXT);
  }, [openConsultation]);

  const openTeacherConsultation = useCallback(
    (teacher) => {
      openConsultation({
        retreat_name: teacher.name,
        teacher_name: teacher.name,
        slug: teacher.slug ? `online-courses/instructor/${teacher.slug}` : "online-courses",
        location: "Online",
        askTeacher: false,
      });
    },
    [openConsultation]
  );

  const goToTeachers = useCallback(() => {
    setActiveNeed(null);
    scrollToId("teachers");
  }, []);

  const goToPrivateTeachers = useCallback(() => {
    setActiveNeed("private");
    scrollToId("teachers");
  }, []);

  const { title, description, ogImage } = ONLINE_COURSES_SEO;
  const canonicalPath = "/online-courses";

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        name: title,
        url: `https://yogaretreatsbooking.com${canonicalPath}`,
        description,
      },
      {
        "@type": "FAQPage",
        mainEntity: faqItems.map((f) => ({
          "@type": "Question",
          name: f.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: f.answer,
          },
        })),
      },
    ],
  };

  return (
    <div className="min-h-screen bg-gray-50/50 pt-16">
      <Seo
        title={title}
        description={description}
        canonicalPath={canonicalPath}
        ogImagePath={ogImage}
        jsonLd={jsonLd}
      />

      <OnlineCoursesHero
        teacherPhotos={heroPhotos}
        onFindTeacher={goToTeachers}
        onBookConsultation={openGeneralConsultation}
      />

      <PrivateYogaSection onFindPrivateTeacher={goToPrivateTeachers} />

      <TrustReasonsSection />

      <TeacherDiscoverySection
        teachers={teachers}
        loading={loading}
        activeNeed={activeNeed}
        onNeedChange={setActiveNeed}
        onBookConsultation={openTeacherConsultation}
        onOpenConsultation={openGeneralConsultation}
      />

      <HowItWorksSection />

      <ConsultationCtaSection onBookConsultation={openGeneralConsultation} />

      <section className="bg-white" aria-labelledby="faq-heading">
        <div className="container mx-auto px-4 py-16 md:py-20">
          <div className="mx-auto max-w-3xl">
            <h2
              id="faq-heading"
              className="text-center text-3xl font-black tracking-tight text-gray-900 md:text-4xl"
            >
              Frequently Asked Questions
            </h2>
            <div className="mt-10 space-y-3">
              {faqItems.map((item) => (
                <details
                  key={item.question}
                  className="group rounded-2xl border border-gray-100 bg-gray-50/50 p-5 open:bg-white open:shadow-sm"
                >
                  <summary className="cursor-pointer list-none font-bold text-gray-900 marker:content-none [&::-webkit-details-marker]:hidden">
                    <span className="flex items-start justify-between gap-4">
                      {item.question}
                      <span className="shrink-0 text-emerald-600 transition-transform group-open:rotate-45">
                        +
                      </span>
                    </span>
                  </summary>
                  <p className="mt-3 text-sm leading-relaxed text-gray-600">{item.answer}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      <FinalCtaSection
        onFindTeacher={goToTeachers}
        onBookConsultation={openGeneralConsultation}
      />

      <StickyMobileCta onFindTeacher={goToTeachers} />

      <FreeConsultationForm
        open={consultOpen}
        onClose={() => setConsultOpen(false)}
        context={consultContext}
      />
    </div>
  );
}

export default OnlineCourses;
